import { topologyRoomBackedPlaneUVAABB } from "building-generation/elements/walls2/planarTopologySelectionGeometry";
import type { BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity } from "building-types/walls2/wallPlaneAssemblyTypes";
import { aabb2ByPolyLine2s, aabb2Grow } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import type { PolyLine2 } from "geom/polyLine2Types";
import {
  planLevelBuildingGraphPlanarTopologyIds,
  wallPlaneSubdomainTouchesPlanViewLevels,
} from "./wallPlaneTopologyPlanLevelFilter";

function planarTopologyCWFaceLoops(topology: BuildingGraphPlanarTopology): PolyLine2[] {
  const planarGraph = topology.planeTopology.planarGraph;
  const faceWindingDirs = planarGraph.faceWindingDirs ?? [];
  const loops: PolyLine2[] = [];

  for (let faceIndex = 0; faceIndex < planarGraph.faceHalfEdges.length; faceIndex++) {
    if (faceWindingDirs[faceIndex] !== WindingDir.CW) {
      continue;
    }
    const loop = planarGraphHalfEdgeStripToPolyLine2(planarGraph, planarGraph.faceHalfEdges[faceIndex]);
    loops.push(loop);
  }

  return loops;
}

export function planarTopologyPlaneUVAABB(topology: BuildingGraphPlanarTopology): AABB2 | undefined {
  const loops = planarTopologyCWFaceLoops(topology);
  if (loops.length === 0) {
    return undefined;
  }

  const unionAabb = aabb2ByPolyLine2s(loops);
  if (!unionAabb) {
    return undefined;
  }

  const searchAabb = { ...unionAabb };
  aabb2Grow(searchAabb, -DIST_EPSILON, -DIST_EPSILON);
  return searchAabb;
}

function subdomainIntersectsTopologyFaceNodes(
  subdomain: WallPlaneSubdomain,
  topologyFaceNodeIds: ReadonlySet<string>,
): boolean {
  for (const faceNodeId of subdomain.faceNodeIds) {
    if (topologyFaceNodeIds.has(faceNodeId)) {
      return true;
    }
  }
  return false;
}

/**
 * Wall-plane subdomains intersecting a planar topology (full height).
 * For plan-level filtering, use {@link planarTopologyWallPlaneSubdomainsTouchingPlanLevels}.
 */
export function planarTopologyWallPlaneSubdomains(
  building: Pick<Building, "buildingGraph" | "wallPlanes">,
  topologyId: string,
): readonly WallPlaneSubdomain[] {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return [];
  }

  const wallPlane = building.wallPlanes[topology.planeHash];
  if (!wallPlane) {
    return [];
  }

  const topologyAabb = topologyRoomBackedPlaneUVAABB(topology, building.buildingGraph);
  if (!topologyAabb) {
    return [];
  }

  const topologyFaceNodeIds = new Set(topology.faceNodeIds);
  const candidates = wallPlane.descriptor.subdomainIndex.search(topologyAabb);
  const matched: WallPlaneSubdomain[] = [];

  for (const subdomain of candidates) {
    if (!subdomainIntersectsTopologyFaceNodes(subdomain, topologyFaceNodeIds)) {
      continue;
    }
    matched.push(subdomain);
  }

  return matched;
}

/** Wall-plane subdomains intersecting a planar topology that touch the given plan levels. */
export function planarTopologyWallPlaneSubdomainsTouchingPlanLevels(
  building: Building,
  topologyId: string,
  planLevelIds: ReadonlySet<string>,
): readonly WallPlaneSubdomain[] {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return [];
  }

  const wallPlane = building.wallPlanes[topology.planeHash];
  if (!wallPlane) {
    return [];
  }

  const topologyAabb = topologyRoomBackedPlaneUVAABB(topology, building.buildingGraph, planLevelIds);
  if (!topologyAabb) {
    return [];
  }

  const topologyFaceNodeIds = new Set(topology.faceNodeIds);
  const candidates = wallPlane.descriptor.subdomainIndex.search(topologyAabb);
  const matched: WallPlaneSubdomain[] = [];

  for (const subdomain of candidates) {
    if (!subdomainIntersectsTopologyFaceNodes(subdomain, topologyFaceNodeIds)) {
      continue;
    }
    if (!wallPlaneSubdomainTouchesPlanViewLevels(subdomain, building, topologyFaceNodeIds, planLevelIds)) {
      continue;
    }
    matched.push(subdomain);
  }

  return matched;
}

/**
 * Wall-plane subdomains that have resolved `wallInfo` and intersect the planar topology,
 * in search order from {@link planarTopologyWallPlaneSubdomainsForOptionalPlanLevels}.
 */
export function collectWallPlaneSubdomainsForPlanarTopology(
  building: Building,
  topologyId: string,
  planLevelIds?: ReadonlySet<string>,
): WallPlaneSubdomain[] {
  return planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(building, topologyId, planLevelIds).filter(
    (subdomain) => subdomain.wallInfo !== null,
  );
}

/** Resolve subdomains for a topology, optionally restricted to plan levels. */
export function planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(
  building: Building,
  topologyId: string,
  planLevelIds?: ReadonlySet<string>,
): readonly WallPlaneSubdomain[] {
  if (planLevelIds === undefined) {
    return planarTopologyWallPlaneSubdomains(building, topologyId);
  }
  return planarTopologyWallPlaneSubdomainsTouchingPlanLevels(building, topologyId, planLevelIds);
}

function collectWallCoreRuleIdsFromSubdomains(subdomains: readonly WallPlaneSubdomain[]): Set<string> {
  const ruleIds = new Set<string>();
  for (const subdomain of subdomains) {
    for (const operation of subdomain.operations) {
      if (operation.wallPlaneOperator.operationType === "SET_WALL_CORE") {
        ruleIds.add(operation.rule.id);
      }
    }
  }
  return ruleIds;
}

export interface PlanarTopologySubdomainUBounds {
  readonly uMin: number;
  readonly uMax: number;
}

export interface PlanarTopologySubdomainVBounds {
  readonly vMin: number;
  readonly vMax: number;
}

export function filterSubdomainsByUBounds(
  subdomains: readonly WallPlaneSubdomain[],
  uBounds: PlanarTopologySubdomainUBounds,
): readonly WallPlaneSubdomain[] {
  return subdomains.filter((subdomain) => subdomain.maxX > uBounds.uMin && subdomain.minX < uBounds.uMax);
}

export function filterSubdomainsByVBounds(
  subdomains: readonly WallPlaneSubdomain[],
  vBounds: PlanarTopologySubdomainVBounds,
): readonly WallPlaneSubdomain[] {
  return subdomains.filter((subdomain) => subdomain.maxY > vBounds.vMin && subdomain.minY < vBounds.vMax);
}

export function collectWallCoreRuleIdsForPlanarTopology(
  building: Building,
  topologyId: string,
  planLevelIds?: ReadonlySet<string>,
  uBounds?: PlanarTopologySubdomainUBounds,
): ReadonlySet<string> {
  let subdomains = planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(building, topologyId, planLevelIds);
  if (uBounds) {
    subdomains = filterSubdomainsByUBounds(subdomains, uBounds);
  }
  return collectWallCoreRuleIdsFromSubdomains(subdomains);
}

export function collectWallCoreRulePlanarTopologyCounts(
  building: Building,
  planLevelIds?: ReadonlySet<string>,
): Record<string, number> {
  const topologyIdsByRuleId = new Map<string, Set<string>>();

  const topologyIdsToScan =
    planLevelIds === undefined
      ? Object.keys(building.buildingGraph.faceTopologies)
      : planLevelBuildingGraphPlanarTopologyIds(building, planLevelIds);

  for (const topologyId of topologyIdsToScan) {
    if (!building.buildingGraph.faceTopologies[topologyId]) {
      continue;
    }
    const ruleIds = collectWallCoreRuleIdsFromSubdomains(
      planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(building, topologyId, planLevelIds),
    );
    for (const ruleId of ruleIds) {
      let topologySet = topologyIdsByRuleId.get(ruleId);
      if (!topologySet) {
        topologySet = new Set<string>();
        topologyIdsByRuleId.set(ruleId, topologySet);
      }
      topologySet.add(topologyId);
    }
  }

  const counts: Record<string, number> = {};
  for (const [ruleId, topologySet] of topologyIdsByRuleId) {
    counts[ruleId] = topologySet.size;
  }
  return counts;
}

export function collectWallCoreAssembliesForPlanarTopology(
  building: Building,
  topologyId: string,
  planLevelIds?: ReadonlySet<string>,
  uBounds?: PlanarTopologySubdomainUBounds,
  vBounds?: PlanarTopologySubdomainVBounds,
): readonly WallPlaneAssemblyEntity[] {
  let subdomains = planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(building, topologyId, planLevelIds);
  if (uBounds) {
    subdomains = filterSubdomainsByUBounds(subdomains, uBounds);
  }
  if (vBounds) {
    subdomains = filterSubdomainsByVBounds(subdomains, vBounds);
  }
  const assembliesById = new Map<string, WallPlaneAssemblyEntity>();

  for (const subdomain of subdomains) {
    const assembly = subdomain.wallInfo?.coreAssembly;
    if (!assembly) {
      continue;
    }
    assembliesById.set(assembly.id, assembly);
  }

  return [...assembliesById.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function collectWallOffsetsForPlanarTopology(
  building: Building,
  topologyId: string,
  planLevelIds?: ReadonlySet<string>,
  uBounds?: PlanarTopologySubdomainUBounds,
  vBounds?: PlanarTopologySubdomainVBounds,
): readonly number[] {
  let subdomains = planarTopologyWallPlaneSubdomainsForOptionalPlanLevels(building, topologyId, planLevelIds);
  if (uBounds) {
    subdomains = filterSubdomainsByUBounds(subdomains, uBounds);
  }
  if (vBounds) {
    subdomains = filterSubdomainsByVBounds(subdomains, vBounds);
  }

  const offsets = new Set<number>();
  for (const subdomain of subdomains) {
    const wallOffset = subdomain.wallInfo?.wallOffset;
    if (wallOffset === undefined) {
      continue;
    }
    offsets.add(wallOffset);
  }

  return [...offsets].sort((a, b) => a - b);
}
